import { useState } from 'react';
import { DateTime, Num, T, Var, useGT } from 'gt-react';
import {
  CheckIcon,
  DownloadSimpleIcon,
  PlayIcon,
  XIcon,
} from '@phosphor-icons/react';
import { BackgroundJobStatus } from '@erp/shared-types';
import {
  FEEDBACK_SUPPLIER_PROFILES,
  HUBBELL_FEEDBACK_DIVISIONS,
  hubbellFeedbackDivisionFromFileName,
  type HubbellFeedbackBuiltDivision,
  type HubbellFeedbackDivisionOutcome,
  type FeedbackSupplierProfile,
  type HubbellFeedbackRun,
} from '@erp/rpc';

import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { FileDropzone } from '../../../item-master/components/FileDropzone';
import { ToolCard } from '../ToolCard';
import type { HubbellFeedbackStartPhase } from '@/api/pricing/startHubbellFeedbackRun';
import type { DropFileState, DropState } from './classifyFiles';
import { FeedbackSupplierProvider } from './feedbackSupplier';
import { useFeedbackSupplier } from './feedbackSupplierContext';
import {
  HUBBELL_FEEDBACK_ACCEPT,
  validateHubbellFeedbackFile,
} from './hubbellFeedbackFile';
import {
  dropProblemText,
  skipReasonText,
  startBlockerText,
  unpairedReasonText,
} from './hubbellFeedbackText';
import {
  divisionLines,
  downloadsAvailable,
  isRunActive,
  type StartBlocker,
} from './runDisplay';

export interface HubbellFeedbackCardViewProps {
  /** Whose results the card takes; Hubbell when absent. */
  supplier?: FeedbackSupplierProfile;
  run: HubbellFeedbackRun | null;
  runLoading: boolean;
  runError: boolean;
  drop: DropState;
  phase: HubbellFeedbackStartPhase | null;
  blocker: StartBlocker | null;
  downloadingTab: string | null;
  /** The dropped FINAL.xlsx already staged by an earlier run; it is not uploaded again. */
  stagedResultsId?: string | null;
  onFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
  /** Marks or unmarks a Division to build with Sales 0. */
  onToggleNoSalesFile: (tab: string) => void;
  onStart: () => void;
  onDownload: (tab: string) => void;
}

function FileKindBadge({
  entry,
  staged,
}: {
  entry: DropFileState;
  staged: boolean;
}) {
  const gt = useGT();
  const { resultsFileLabel } = useFeedbackSupplier();
  if (entry.problem) {
    const text = dropProblemText(entry.problem, gt, resultsFileLabel);
    return (
      <Badge variant="warning" title={text}>
        {text}
      </Badge>
    );
  }
  if (entry.kind === 'reading') return <Spinner size="sm" />;
  if (entry.kind === 'results') {
    return staged ? (
      <Badge variant="default">
        <T context="Hubbell Feedback card: badge on a FINAL.xlsx already uploaded by an earlier run and reused">
          Results · uploaded
        </T>
      </Badge>
    ) : (
      <Badge variant="default">
        <T context="Hubbell Feedback card: badge on the dropped FINAL.xlsx">
          Results
        </T>
      </Badge>
    );
  }
  return (
    <Badge variant="secondary">
      <T context="Hubbell Feedback card: badge on a dropped Customer Sales Summary">
        Customer Summary
      </T>
    </Badge>
  );
}

function DroppedFileRow({
  entry,
  staged,
  onRemove,
}: {
  entry: DropFileState;
  staged: boolean;
  onRemove: (id: string) => void;
}) {
  const gt = useGT();
  return (
    <li className="flex items-center gap-2 text-xs">
      <span className="min-w-0 flex-1 truncate" title={entry.file.name}>
        {entry.file.name}
      </span>
      <FileKindBadge entry={entry} staged={staged} />
      <Button
        variant="ghost"
        size="icon"
        className="size-6"
        aria-label={gt('Remove {name}', {
          context: 'Hubbell Feedback card: remove one dropped file',
          name: entry.file.name,
        })}
        onClick={() => onRemove(entry.id)}
      >
        <XIcon className="h-4 w-4" />
      </Button>
    </li>
  );
}

type Division = (typeof HUBBELL_FEEDBACK_DIVISIONS)[number];

const DIVISION_BY_LETTER = new Map(
  HUBBELL_FEEDBACK_DIVISIONS.map((division) => [division.letter, division]),
);

/** The tabs a run was started for: its sales files' Divisions and the Sales 0 ones. */
function plannedTabs(run: HubbellFeedbackRun): ReadonlySet<string> {
  const tabs = new Set(run.inputs.noSalesFileTabs);
  for (const file of run.inputs.salesFiles) {
    const division = hubbellFeedbackDivisionFromFileName(file.fileName);
    if (division) tabs.add(division.letter);
  }
  return tabs;
}

function StartPhaseLine({ phase }: { phase: HubbellFeedbackStartPhase }) {
  if (phase.phase === 'starting') {
    return (
      <p className="text-muted-foreground text-xs">
        <T context="Hubbell Feedback card: the files are uploaded and the run is being started">
          Starting the run…
        </T>
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <Progress value={phase.percent} />
      <p className="text-muted-foreground truncate text-xs">
        <T context="Hubbell Feedback card: upload progress line">
          Uploading <Var>{phase.fileName}</Var>… <Num>{phase.percent}</Num>%
        </T>
      </p>
    </div>
  );
}

function RunStatusBadge({ status }: { status: BackgroundJobStatus }) {
  switch (status) {
    case BackgroundJobStatus.Queued:
      return (
        <Badge variant="outline">
          <T context="Hubbell Feedback card: run status">Queued</T>
        </Badge>
      );
    case BackgroundJobStatus.Running:
      return (
        <Badge variant="outline">
          <T context="Hubbell Feedback card: run status">Running</T>
        </Badge>
      );
    case BackgroundJobStatus.Succeeded:
      return (
        <Badge variant="success">
          <T context="Hubbell Feedback card: run status">Done</T>
        </Badge>
      );
    case BackgroundJobStatus.Failed:
      return (
        <Badge variant="destructive">
          <T context="Hubbell Feedback card: run status">Failed</T>
        </Badge>
      );
  }
}

function DivisionDownloadButton({
  outcome,
  downloading,
  onDownload,
}: {
  outcome: HubbellFeedbackBuiltDivision;
  downloading: boolean;
  onDownload: (tab: string) => void;
}) {
  const gt = useGT();
  return (
    <Button
      variant="outline"
      size="icon"
      className="size-6"
      disabled={downloading}
      aria-label={gt('Download {fileName}', {
        context: "Hubbell Feedback card: download one Division's Feedback File",
        fileName: outcome.fileName,
      })}
      onClick={() => onDownload(outcome.tab)}
    >
      {downloading ? (
        <Spinner size="sm" />
      ) : (
        <DownloadSimpleIcon className="h-4 w-4" />
      )}
    </Button>
  );
}

/** What the drop says a Division will get: its file (with its remove), Sales 0, or nothing yet. */
function DivisionPlanLine({
  file,
  noFile,
  disabled,
  onRemove,
}: {
  file: DropFileState | undefined;
  noFile: boolean;
  disabled: boolean;
  onRemove: (id: string) => void;
}) {
  const gt = useGT();
  if (file)
    return (
      <span className="flex items-center gap-1">
        <span
          className="text-muted-foreground min-w-0 flex-1 truncate"
          title={file.file.name}
        >
          {file.file.name}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-5 shrink-0"
          disabled={disabled}
          aria-label={gt('Remove {name}', {
            context: 'Hubbell Feedback card: remove one dropped file',
            name: file.file.name,
          })}
          onClick={() => onRemove(file.id)}
        >
          <XIcon className="h-3 w-3" />
        </Button>
      </span>
    );
  if (noFile)
    return (
      <span className="text-primary">
        <T context="Hubbell Feedback card: a Division card marked to build with Sales 0">
          Sales 0
        </T>
      </span>
    );
  return (
    <span className="text-muted-foreground">
      <T context="Hubbell Feedback card: a Division card with no Customer Sales Summary yet; clicking offers to build it with Sales 0">
        No sales file
      </T>
    </span>
  );
}

/** "1 Load" or "N Loads". */
function LoadCount({ count }: { count: number }) {
  return count === 1 ? (
    <T context="Hubbell Feedback card: a Division's Load count when it is one">
      1 Load
    </T>
  ) : (
    <T context="Hubbell Feedback card: a Division's Load count">
      <Num>{count}</Num> Loads
    </T>
  );
}

/** What the latest run made of a Division: its file to download, why it was skipped, or that it is being built. */
function DivisionResultLine({
  outcome,
  pending,
  planning,
  downloadable,
  downloading,
  onDownload,
}: {
  outcome: HubbellFeedbackDivisionOutcome | null;
  /** The run is going and has not reached this Division yet. */
  pending: boolean;
  /**
   * A new run is being planned above this line, so the result is the last
   * run's: a skip says nothing useful then, and a built file is labelled.
   */
  planning: boolean;
  downloadable: boolean;
  downloading: boolean;
  onDownload: (tab: string) => void;
}) {
  const gt = useGT();
  const { resultsFileLabel } = useFeedbackSupplier();
  if (outcome === null) {
    return pending ? (
      <span className="text-muted-foreground flex items-center gap-1">
        <Spinner size="sm" />
        <T context="Hubbell Feedback card: a Division the run is building">
          Building…
        </T>
      </span>
    ) : null;
  }
  if (outcome.status === 'skipped')
    return planning ? null : (
      <span className="text-muted-foreground truncate">
        {skipReasonText(outcome.reason, gt, resultsFileLabel)}
      </span>
    );
  return (
    <span className="flex items-center gap-1">
      <span className="min-w-0 flex-1 truncate">
        {planning ? (
          <span className="text-muted-foreground">
            <T context="Hubbell Feedback card: prefix on a Division's result when it comes from the previous run, while a new one is being planned">
              Last run ·{' '}
            </T>
          </span>
        ) : null}
        {outcome.salesFileName === null ? (
          <T context="Hubbell Feedback card: a Division built with Sales 0 and its Load count">
            Sales 0 · <LoadCount count={outcome.loadCount} />
          </T>
        ) : (
          <T context="Hubbell Feedback card: a built Division's Load count and unmatched count">
            <LoadCount count={outcome.loadCount} /> ·{' '}
            <Num>{outcome.unmatchedCount}</Num> unmatched
          </T>
        )}
      </span>
      {downloadable ? (
        <DivisionDownloadButton
          outcome={outcome}
          downloading={downloading}
          onDownload={onDownload}
        />
      ) : null}
    </span>
  );
}

function DivisionCardHeader({
  code,
  name,
  selected,
}: {
  code: string;
  name: string;
  selected: boolean;
}) {
  return (
    <span className="flex items-center gap-1">
      <span className="bg-muted shrink-0 rounded px-1 font-mono">{code}</span>
      <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
      {selected ? (
        <CheckIcon className="text-primary h-4 w-4 shrink-0" />
      ) : null}
    </span>
  );
}

/** The plan part of a card: the header alone, the header with its file, or a button that marks the Division Sales 0. */
function DivisionCardPlan({
  code,
  name,
  planning,
  selected,
  file,
  noFile,
  disabled,
  onClick,
  onRemove,
}: {
  code: string;
  name: string;
  planning: boolean;
  selected: boolean;
  file: DropFileState | undefined;
  noFile: boolean;
  disabled: boolean;
  onClick: () => void;
  onRemove: (id: string) => void;
}) {
  const gt = useGT();
  const header = (
    <DivisionCardHeader code={code} name={name} selected={selected} />
  );
  if (!planning) return header;
  if (file)
    return (
      <>
        {header}
        <DivisionPlanLine
          file={file}
          noFile={noFile}
          disabled={disabled}
          onRemove={onRemove}
        />
      </>
    );
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={selected}
      aria-label={gt('{division} ({code})', {
        context: 'Hubbell Feedback card: a Division card; click to build it',
        division: name,
        code,
      })}
      onClick={onClick}
      className={cn(
        'flex min-w-0 flex-col gap-0.5 rounded text-left',
        !disabled && 'hover:bg-muted/50',
      )}
    >
      {header}
      <DivisionPlanLine
        file={undefined}
        noFile={noFile}
        disabled={disabled}
        onRemove={onRemove}
      />
    </button>
  );
}

/**
 * One Division's card: what the drop plans for it (click to build with
 * Sales 0 when it has no file), and what the latest run made of it, with
 * its Download.
 */
function DivisionCard({
  tab,
  division,
  planning,
  file,
  noFile,
  outcome,
  pending,
  disabled,
  downloadable,
  downloading,
  onClick,
  onRemove,
  onDownload,
}: {
  tab: string;
  division: Division | undefined;
  /** FINAL.xlsx is on the card, so the plan line and the click apply. */
  planning: boolean;
  file: DropFileState | undefined;
  noFile: boolean;
  outcome: HubbellFeedbackDivisionOutcome | null;
  pending: boolean;
  disabled: boolean;
  downloadable: boolean;
  downloading: boolean;
  onClick: () => void;
  onRemove: (id: string) => void;
  onDownload: (tab: string) => void;
}) {
  const selected = planning && (file !== undefined || noFile);
  return (
    <li
      className={cn(
        'flex min-w-0 flex-col gap-0.5 rounded-md border p-2 text-xs transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-dashed',
      )}
    >
      <DivisionCardPlan
        code={division?.code ?? tab}
        name={division?.name ?? tab}
        planning={planning}
        selected={selected}
        file={file}
        noFile={noFile}
        disabled={disabled}
        onClick={onClick}
        onRemove={onRemove}
      />
      <DivisionResultLine
        outcome={outcome}
        pending={pending}
        planning={planning && !pending}
        downloadable={downloadable}
        downloading={downloading}
        onDownload={onDownload}
      />
    </li>
  );
}

/**
 * The nine Divisions as cards. With FINAL.xlsx on the card, a Division whose
 * Customer Sales Summary was dropped is in, and clicking one without a file
 * asks whether to build it with Sales 0 (clicking again takes it out). The
 * latest run's result and Download sit on the same cards.
 */
function DivisionCards({
  drop,
  run,
  disabled,
  downloadingTab,
  onToggleNoSalesFile,
  onRemove,
  onDownload,
}: {
  drop: DropState;
  run: HubbellFeedbackRun | null;
  disabled: boolean;
  downloadingTab: string | null;
  onToggleNoSalesFile: (tab: string) => void;
  onRemove: (id: string) => void;
  onDownload: (tab: string) => void;
}) {
  const [confirming, setConfirming] = useState<Division | null>(null);
  const planning = drop.resultsWorkbook !== null;
  const active = run !== null && isRunActive(run);
  const downloadable = run !== null && downloadsAvailable(run);
  const planned = run ? plannedTabs(run) : new Set<string>();
  const noSalesFile = new Set(drop.noSalesFileTabs);
  const lines = run
    ? divisionLines(run)
    : HUBBELL_FEEDBACK_DIVISIONS.map((division) => ({
        tab: division.letter,
        division: division.name,
        outcome: null,
      }));
  return (
    <>
      <ul className="grid grid-cols-3 gap-2">
        {lines.map((line) => {
          const division = DIVISION_BY_LETTER.get(line.tab);
          const file = drop.salesFileByTab.get(line.tab);
          const noFile = noSalesFile.has(line.tab);
          return (
            <DivisionCard
              key={line.tab}
              tab={line.tab}
              division={division}
              planning={planning}
              file={file}
              noFile={noFile}
              outcome={line.outcome}
              pending={active && line.outcome === null && planned.has(line.tab)}
              disabled={disabled}
              downloadable={downloadable}
              downloading={downloadingTab === line.tab}
              onClick={() =>
                noFile
                  ? onToggleNoSalesFile(line.tab)
                  : division && setConfirming(division)
              }
              onRemove={onRemove}
              onDownload={onDownload}
            />
          );
        })}
      </ul>
      <AlertDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="Hubbell Feedback card: title of the dialog that confirms building a Division with Sales 0">
                Generate feedback for <Var>{confirming?.name}</Var> with Sales
                0?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="Hubbell Feedback card: body of the dialog that confirms building a Division with Sales 0">
                No Customer Sales Summary is attached for this Division, so
                every Load on its Feedback File will show Sales 0. Drop its
                sales file instead to get real Sales.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <T context="Hubbell Feedback card: cancel building a Division with Sales 0">
                Cancel
              </T>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirming) onToggleNoSalesFile(confirming.letter);
                setConfirming(null);
              }}
            >
              <T context="Hubbell Feedback card: confirm building a Division with Sales 0">
                Generate With Sales 0
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** What stands between the viewer and the files: expired, someone else's, or a failed run. */
function RunNotice({ run }: { run: HubbellFeedbackRun }) {
  const gt = useGT();
  const succeeded = run.status === BackgroundJobStatus.Succeeded;
  return (
    <>
      {succeeded && run.startedByCurrentUser && !downloadsAvailable(run) ? (
        <p className="text-muted-foreground text-xs">
          <T context="Hubbell Feedback card: the latest run's files have passed the 30-day retention">
            The Feedback Files of this run have expired; start a run to get
            fresh ones.
          </T>
        </p>
      ) : null}
      {succeeded && !run.startedByCurrentUser ? (
        <p className="text-muted-foreground text-xs">
          <T context="Hubbell Feedback card: the latest run was started by someone else, whose downloads these are">
            Started by another user; the downloads are theirs. Start a run to
            get your own.
          </T>
        </p>
      ) : null}
      {run.status === BackgroundJobStatus.Failed ? (
        <AlertContainer variant="destructive">
          <AlertTitle>
            <T context="Hubbell Feedback card: the run failed">
              The run failed
            </T>
          </AlertTitle>
          <AlertDescription>
            {run.errorMessage ??
              gt('Something went wrong; try the drop again.', {
                context:
                  'Hubbell Feedback card: a failed run with no message from the server',
              })}
          </AlertDescription>
        </AlertContainer>
      ) : null}
    </>
  );
}

/** One line for the latest run: its status and when; a bar while it goes. */
function RunStatusLine({ run }: { run: HubbellFeedbackRun }) {
  const gt = useGT();
  const { resultsFileLabel } = useFeedbackSupplier();
  const unpaired = run.report?.unpairedFiles ?? [];
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-medium">
          <T context="Hubbell Feedback card: heading of the latest run section">
            Latest run
          </T>
        </span>
        <RunStatusBadge status={run.status} />
        <span className="text-muted-foreground">
          <DateTime>{run.createdAt}</DateTime>
        </span>
      </div>
      {isRunActive(run) ? (
        <Progress
          value={run.percent ?? 100}
          className={run.percent === null ? 'animate-pulse' : undefined}
        />
      ) : null}
      {unpaired.length > 0 ? (
        <p className="text-muted-foreground truncate text-xs">
          <T context="Hubbell Feedback card: sales files of the run that paired with no Division tab">
            Not used:{' '}
            <Var>
              {unpaired
                .map(
                  (file) =>
                    `${file.fileName} · ${unpairedReasonText(file.reason, gt, resultsFileLabel)}`,
                )
                .join('; ')}
            </Var>
          </T>
        </p>
      ) : null}
      <RunNotice run={run} />
    </div>
  );
}

/**
 * FINAL.xlsx on one line; placed sales files sit on their Division cards;
 * only files that could not be placed are listed, with why.
 */
function UnplacedFiles({
  drop,
  stagedResultsId,
  onRemove,
}: {
  drop: DropState;
  stagedResultsId: string | null;
  onRemove: (id: string) => void;
}) {
  const rows = drop.files.flatMap((entry) =>
    entry.tab === null || entry.problem ? (
      <DroppedFileRow
        key={entry.id}
        entry={entry}
        staged={entry.id === stagedResultsId}
        onRemove={onRemove}
      />
    ) : (
      []
    ),
  );
  if (rows.length === 0) return null;
  return <ul className="flex flex-col gap-1">{rows}</ul>;
}

/** The upload progress while a start goes, the Start button, and why it is disabled. */
function StartControls({
  blocker,
  phase,
  onStart,
}: {
  blocker: StartBlocker | null;
  phase: HubbellFeedbackStartPhase | null;
  onStart: () => void;
}) {
  const gt = useGT();
  const blockerText = blocker ? startBlockerText(blocker, gt) : null;
  return (
    <>
      {phase ? <StartPhaseLine phase={phase} /> : null}
      <div className="flex flex-col gap-1">
        <Button
          size="sm"
          disabled={blocker !== null}
          onClick={onStart}
          className="self-start"
        >
          <PlayIcon className="h-4 w-4" />
          <T context="Hubbell Feedback card: button that uploads the files and starts the run">
            Start Run
          </T>
        </Button>
        {blockerText ? (
          <p className="text-muted-foreground text-xs">{blockerText}</p>
        ) : null}
      </div>
    </>
  );
}

/** The latest run's status line, or that it is loading or could not be loaded. */
function LatestRunState({
  run,
  loading,
  error,
}: {
  run: HubbellFeedbackRun | null;
  loading: boolean;
  error: boolean;
}) {
  if (loading) return <Spinner size="sm" />;
  if (error)
    return (
      <p className="text-destructive text-xs">
        <T context="Hubbell Feedback card: the latest run could not be loaded">
          The latest run could not be loaded.
        </T>
      </p>
    );
  return run ? <RunStatusLine run={run} /> : null;
}

/**
 * The fourth tool card: drop FINAL.xlsx and the Customer Sales Summaries,
 * start the run, watch it, and download one Feedback File per Division. The
 * org's latest run is shown whether or not this page started it.
 */
export function HubbellFeedbackCardView({
  supplier = FEEDBACK_SUPPLIER_PROFILES.hubbell,
  run,
  runLoading,
  runError,
  drop,
  phase,
  blocker,
  downloadingTab,
  stagedResultsId = null,
  onFiles,
  onRemove,
  onToggleNoSalesFile,
  onStart,
  onDownload,
}: HubbellFeedbackCardViewProps) {
  const gt = useGT();
  const starting = phase !== null;
  return (
    <FeedbackSupplierProvider supplier={supplier}>
      <ToolCard
        fixedHeight={false}
        title={
          <T context="heading of a supplier's Feedback card, e.g. Hubbell Feedback">
            <Var>{supplier.name}</Var> Feedback
          </T>
        }
        description={
          <T context="explanation under a supplier's Feedback card heading; the variable names the supplier's Results Workbook">
            Drop <Var>{supplier.resultsFileLabel}</Var> and the Customer Sales
            Summaries, then pick the Divisions to build. One Feedback File per
            Division.
          </T>
        }
      >
        <div className="flex flex-col gap-2">
          <FileDropzone
            inline
            className="w-full max-w-sm"
            accept={HUBBELL_FEEDBACK_ACCEPT}
            disabled={starting}
            prompt={
              <T context="instruction inside a supplier's Feedback drop area; the variable names the supplier's Results Workbook">
                Drop <Var>{supplier.resultsFileLabel}</Var> and the Customer
                Sales Summaries here.
              </T>
            }
            validate={(file) =>
              Promise.resolve(validateHubbellFeedbackFile(file, gt, supplier))
            }
            multiple
            onFilesAccepted={onFiles}
          />
          <UnplacedFiles
            drop={drop}
            stagedResultsId={stagedResultsId}
            onRemove={onRemove}
          />
          {/* Always there: the latest run's files stay downloadable with nothing dropped. */}
          <DivisionCards
            drop={drop}
            run={run}
            disabled={starting}
            downloadingTab={downloadingTab}
            onToggleNoSalesFile={onToggleNoSalesFile}
            onRemove={onRemove}
            onDownload={onDownload}
          />
          <StartControls blocker={blocker} phase={phase} onStart={onStart} />
          <LatestRunState run={run} loading={runLoading} error={runError} />
        </div>
      </ToolCard>
    </FeedbackSupplierProvider>
  );
}
